import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, MapPin, Check } from "lucide-react";
import { COMPANY, V2_IMAGES } from "@/data/v2";
import { getCity, CITY_LIST } from "@/data/cities";
import {
  NavbarLight, TrustBar, ServicesLight, ProcessLight,
  TestimonialsLight, FaqLight, QuoteFormLight, FooterLight,
} from "@/pages/LightHome";
import { StickyCall } from "@/components/site/StickyCall";

const BASE_URL = "https://mhetancheite.pro";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const scrollToQuote = () => document.querySelector("#v2-devis")?.scrollIntoView({ behavior: "smooth" });

const HIGHLIGHTS = [
  "Étanchéité toiture terrasse & balcon",
  "Recherche et réparation de fuite",
  "Résine d'étanchéité & isolation thermique",
  "Devis gratuit sous 24 h · Garantie décennale",
];

export const CityPage = () => {
  const { city } = useParams();
  const data = getCity(city);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!data) return;
    const title = `Étanchéité ${data.name} | Toiture terrasse & réparation de fuite — MH Étanchéité`;
    const desc = `Étancheur à ${data.name} : étanchéité de toiture terrasse, balcon, murs enterrés, résine, isolation thermique et recherche & réparation de fuite. Devis gratuit, garantie décennale. MH Étanchéité intervient à ${data.name} et dans tout le Var et les Alpes-Maritimes.`;
    const prevTitle = document.title;
    document.title = title;
    setMeta("description", desc);
    setCanonical(`${BASE_URL}/etancheite/${data.slug}`);
    return () => { document.title = prevTitle; };
  }, [data]);

  if (!data) return <Navigate to="/" replace />;

  const nearby = CITY_LIST.filter((c) => c.slug !== data.slug).slice(0, 12);

  return (
    <div className="bg-mh2-cream font-body text-mh2-ink min-h-screen">
      <NavbarLight />
      <main>
        {/* City hero */}
        <section className="relative pt-32 lg:pt-40 pb-20 overflow-hidden">
          <div className="absolute top-0 right-0 w-[45%] h-full bg-mh2-surface -z-10 hidden lg:block rounded-bl-[80px]" />
          <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-mh2-blue/5 text-mh2-blue text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-full">
                <MapPin size={14} /> Étanchéité · {data.name}
              </div>
              <h1 className="mt-6 font-heading font-extrabold tracking-tighter text-mh2-ink text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
                Étanchéité à <span className="text-mh2-blue">{data.name}</span>
              </h1>
              <p className="mt-6 text-lg text-mh2-slate max-w-lg leading-relaxed">
                MH Étanchéité, votre étancheur à {data.name}, réalise l'étanchéité de toiture terrasse,
                balcon et murs enterrés, la résine, l'isolation thermique et la recherche &amp; réparation
                de fuite. Devis gratuit, garantie décennale.
              </p>
              <ul className="mt-6 space-y-2">
                {HIGHLIGHTS.map((h) => (
                  <li key={h} className="flex items-center gap-3 text-mh2-ink">
                    <Check size={18} className="text-mh2-green shrink-0" /> {h}
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-wrap gap-4">
                <button onClick={scrollToQuote} className="group inline-flex items-center gap-2 bg-mh2-terra hover:bg-mh2-terraHover text-white font-semibold px-7 py-4 rounded-full transition-colors" data-testid="city-quote-btn">
                  Devis gratuit à {data.name} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-2 border-2 border-mh2-blue text-mh2-blue font-semibold px-7 py-4 rounded-full hover:bg-mh2-blue hover:text-white transition-colors" data-testid="city-call-btn">
                  <Phone size={18} /> {COMPANY.phoneDisplay}
                </a>
              </div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
              <img src={V2_IMAGES.hero} alt={`Étanchéité toiture terrasse à ${data.name}`} fetchpriority="high" decoding="async" className="w-full h-[420px] lg:h-[520px] object-cover rounded-[32px] shadow-2xl shadow-mh2-blue/20" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-3 border border-mh2-border">
                <div className="w-12 h-12 rounded-full bg-mh2-green/10 flex items-center justify-center"><ShieldCheck className="text-mh2-green" /></div>
                <div>
                  <div className="font-heading font-bold text-mh2-ink leading-tight">Garantie décennale</div>
                  <div className="text-xs text-mh2-slate">Travaux assurés 10 ans</div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <TrustBar />

        {/* SEO intro text */}
        <section className="max-w-[900px] mx-auto px-6 lg:px-10 pt-16">
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight text-mh2-ink">
            Votre spécialiste de l'étanchéité à {data.name}
          </h2>
          <p className="mt-4 text-mh2-slate leading-relaxed">
            Vous constatez une infiltration ou une fuite sur votre toiture terrasse à {data.name} ? Vous
            construisez ou rénovez et cherchez une étanchéité fiable et durable ? MH Étanchéité intervient
            à {data.name} pour l'étanchéité de toitures terrasses, balcons, murs enterrés, la pose de
            couvertines, de dalles sur plots, l'application de résine d'étanchéité et l'isolation thermique.
            Nos étancheurs recherchent l'origine des fuites et appliquent la solution adaptée, dans le
            respect des règles de l'art. Tous nos travaux sont couverts par la garantie décennale.
          </p>
        </section>

        <ServicesLight />
        <ProcessLight />
        <TestimonialsLight />
        <FaqLight />

        {/* Internal linking to other cities */}
        <section className="max-w-[1300px] mx-auto px-6 lg:px-10 py-16">
          <h2 className="font-heading font-bold text-xl text-mh2-ink mb-5">Étanchéité dans les villes voisines</h2>
          <div className="flex flex-wrap gap-3">
            {nearby.map((c) => (
              <Link key={c.slug} to={`/etancheite/${c.slug}`} className="inline-flex items-center gap-2 bg-mh2-surface border border-mh2-border px-4 py-2.5 rounded-full text-sm font-medium text-mh2-ink hover:border-mh2-blue hover:text-mh2-blue transition-colors" data-testid={`city-link-${c.slug}`}>
                <MapPin size={14} className="text-mh2-terra" /> Étanchéité {c.name}
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Link to="/" className="text-mh2-blue font-semibold hover:underline" data-testid="city-home-link">← Retour à l'accueil</Link>
          </div>
        </section>

        <QuoteFormLight />
      </main>
      <FooterLight />
      <StickyCall />
    </div>
  );
};
